import { onScopeDispose, watch } from 'vue'
import { useScreenLockStore } from '@/stores/screenLock'

// 2 つの composable は watch と onScopeDispose だけで組み立てている（onBeforeUnmount 等のコンポーネント専用 API を使わない）。
// pinia の setup store も effect scope の中で動くため、store の中からも使える（stores/drawerNavigation.ts）。
// 使い分けは docs/screenLock.md を参照

/**
 * 手動の画面ロック（処理型）
 * @description
 *  API 通信・遷移アニメーション・ページ遷移の前など、処理の前後で呼び出し側が取得・解除する。
 *  「開いている間」のような状態に連動させたい場合は useConditionalScreenLock を使うこと。
 *  acquireLock を呼ぶたびにロックを 1 つ取得し、スコープの破棄（コンポーネントの unmount 等）で残っている分をすべて手放す。
 */
export const useManualScreenLock = () => {
  const screenLockStore = useScreenLockStore()

  // このインスタンスで取得し、まだ手放していないロック
  const releases = new Set<() => void>()

  /**
   * ロックを取得する
   * @param option 表示効果（未指定の項目は false）
   * @return 今回取得したロックだけを手放す関数。何度呼んでも結果は同じ
   * @description
   *  呼ぶたびに別のロックとして取得する。同じ処理が重なって走っても（連打・並行実行）、戻り値で手放すのは自分の分だけなので、
   *  先に終わった処理が後から始まった処理のロックを外すことはない
   */
  const acquireLock = (option: ScreenLockOption = {}): (() => void) => {
    const releaseStoreLock = screenLockStore.acquireLock(option)

    const release = () => {
      releaseStoreLock()
      releases.delete(release)
    }

    releases.add(release)

    return release
  }

  // スコープの破棄時に、このインスタンスで取得して手放していないロックをすべて手放す（他で取得されたロックには触れない）。
  // 呼び出し側には公開しない。公開すると、重なって走った別の処理のロックまで外す使い方ができてしまうため
  onScopeDispose(() => {
    releases.forEach(release => release())
  })

  return {
    acquireLock,
  }
}

/**
 * 条件付きの画面ロック（状態型）
 * @param condition ロックを持つ条件。true を返す間ロックを持ち、false になったら手放す
 * @param option 表示効果。props 等に追従させる場合は getter で渡す
 * @description
 *  モーダル・ドロワーなど「開いている間」ロックしたい場合に使う。初期値も拾うため、最初から開いた状態で描画されてもロックされる。
 *  呼び出し側でロックを直接取得・解除しないこと（条件と食い違い、他のロックを巻き込む原因になる）。
 */
export const useConditionalScreenLock = (
  condition: () => boolean,
  option: ScreenLockOption | (() => ScreenLockOption) = {},
): void => {
  const screenLockStore = useScreenLockStore()

  let release: (() => void) | null = null

  const resolveOption = (): ScreenLockOption => {
    return typeof option === 'function' ? option() : option
  }

  watch(
    () => condition() ? resolveOption() : null,
    (currentOption) => {
      // 差し替え時に一瞬ロックが途切れないよう、新しいロックを取得してから古いロックを手放す
      const previousRelease = release
      release = currentOption
        ? screenLockStore.acquireLock(currentOption, 'conditional')
        : null
      previousRelease?.()
    },
    { immediate: true },
  )

  onScopeDispose(() => {
    release?.()
    release = null
  })
}
